import {
  createActorParticipantId,
  createUserParticipantId,
  resolveParticipantId,
} from '../../../domain'
import {
  PRESENCE_EVENT_TYPES,
  RTC_SESSION_EVENT_TYPES,
} from '../../../ports'
import { RTC_PROVIDERS } from '../../../providerIds'
import { isHumanParticipantIdentityKeyPrefix } from '../participantIdentityContract'
import { createZoomRtcSessionHandle } from './createZoomRtcSessionHandle'

function createEventBus() {
  const listeners = new Set()

  return {
    emit(event) {
      listeners.forEach((listener) => {
        listener(event)
      })
    },
    subscribe(listener) {
      listeners.add(listener)

      return () => {
        listeners.delete(listener)
      }
    },
    clear() {
      listeners.clear()
    },
  }
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizeZoomUserId(value) {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  return String(value).trim()
}

function normalizeLocalParticipantIdentity(localParticipantIdentity = {}) {
  const participantId = resolveParticipantId(localParticipantIdentity)

  if (!participantId) {
    return null
  }

  return {
    participantId,
    userId: normalizeOptionalString(localParticipantIdentity?.userId),
    actorType: normalizeOptionalString(localParticipantIdentity?.actorType),
    actorId: normalizeOptionalString(
      localParticipantIdentity?.actorId ?? localParticipantIdentity?.stableActorId,
    ),
    displayName: normalizeOptionalString(localParticipantIdentity?.displayName),
    profilePicId: normalizeOptionalString(
      localParticipantIdentity?.profilePicId ?? localParticipantIdentity?.avatarImageId,
    ),
    role: normalizeOptionalString(localParticipantIdentity?.role),
  }
}

function parseParticipantMetadata(metadata) {
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata
  }

  const normalizedMetadata = normalizeOptionalString(metadata)
  if (!normalizedMetadata) {
    return {}
  }

  try {
    const parsedMetadata = JSON.parse(normalizedMetadata)
    if (parsedMetadata && typeof parsedMetadata === 'object' && !Array.isArray(parsedMetadata)) {
      return parsedMetadata
    }
  } catch (error) {
    return {
      rawMetadata: normalizedMetadata,
    }
  }

  return {
    rawMetadata: normalizedMetadata,
  }
}

function parseIdentityKey(identityKey) {
  const normalizedIdentityKey = normalizeOptionalString(identityKey)

  if (!normalizedIdentityKey || !normalizedIdentityKey.includes(':')) {
    return null
  }

  const [rawPrefix, ...rawStableIdParts] = normalizedIdentityKey.split(':')
  const prefix = normalizeOptionalString(rawPrefix)
  const stableId = normalizeOptionalString(rawStableIdParts.join(':'))

  if (!prefix || !stableId) {
    return null
  }

  return {
    prefix,
    stableId,
  }
}

function createZoomTransport(userId, metadata = {}, extraTransport = {}) {
  const userIdKey = normalizeZoomUserId(userId)

  return {
    providerId: RTC_PROVIDERS.zoomVideoSdk,
    userId: userIdKey,
    zoomUserId: userIdKey,
    identityKey: normalizeOptionalString(metadata?.identityKey),
    profilePicId: normalizeOptionalString(
      metadata?.profilePicId ?? metadata?.avatarImageId,
    ),
    ...extraTransport,
  }
}

function deriveParticipantIdentity({
  userId,
  metadata = {},
  isLocal = false,
  localParticipantIdentity = null,
  allowTransportFallback = true,
}) {
  const normalizedLocalIdentity = normalizeLocalParticipantIdentity(localParticipantIdentity)

  if (isLocal && normalizedLocalIdentity) {
    return normalizedLocalIdentity
  }

  const resolvedMetadataParticipantId = resolveParticipantId(metadata)
  if (resolvedMetadataParticipantId) {
    return {
      participantId: resolvedMetadataParticipantId,
      userId: normalizeOptionalString(metadata?.userId),
      actorType: normalizeOptionalString(metadata?.actorType),
      actorId: normalizeOptionalString(metadata?.actorId ?? metadata?.stableActorId),
      role: normalizeOptionalString(metadata?.role),
      displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
      profilePicId: normalizeOptionalString(
        metadata?.profilePicId ?? metadata?.avatarImageId,
      ),
    }
  }

  const parsedIdentityKey = parseIdentityKey(metadata?.identityKey)
  if (parsedIdentityKey) {
    if (isHumanParticipantIdentityKeyPrefix(parsedIdentityKey.prefix)) {
      return {
        participantId: createUserParticipantId(parsedIdentityKey.stableId),
        userId: parsedIdentityKey.stableId,
        actorType: 'user',
        actorId: parsedIdentityKey.stableId,
        role: normalizeOptionalString(metadata?.role, parsedIdentityKey.prefix),
        displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
        profilePicId: normalizeOptionalString(
          metadata?.profilePicId ?? metadata?.avatarImageId,
        ),
      }
    }

    return {
      participantId: createActorParticipantId(
        parsedIdentityKey.prefix,
        parsedIdentityKey.stableId,
      ),
      actorType: parsedIdentityKey.prefix,
      actorId: parsedIdentityKey.stableId,
      role: normalizeOptionalString(metadata?.role, parsedIdentityKey.prefix),
      displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
      profilePicId: normalizeOptionalString(
        metadata?.profilePicId ?? metadata?.avatarImageId,
      ),
    }
  }

  if (!allowTransportFallback || !userId) {
    return null
  }

  return {
    participantId: createActorParticipantId('zoom-user', normalizeZoomUserId(userId)),
    actorType: 'zoom-user',
    actorId: normalizeZoomUserId(userId),
    role: normalizeOptionalString(metadata?.role, 'participant'),
    displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
    profilePicId: normalizeOptionalString(
      metadata?.profilePicId ?? metadata?.avatarImageId,
    ),
    transportIdentityFallback: true,
  }
}

function resolveZoomAudioEnabled(snapshotParticipant = {}) {
  if (typeof snapshotParticipant?.audioMuted === 'boolean') {
    return !snapshotParticipant.audioMuted
  }

  if (typeof snapshotParticipant?.muted === 'boolean') {
    return snapshotParticipant.muted === false
  }

  return snapshotParticipant?.audio === 'computer'
}

function resolveZoomVideoEnabled(snapshotParticipant = {}) {
  if (typeof snapshotParticipant?.videoEnabled === 'boolean') {
    return snapshotParticipant.videoEnabled
  }

  return snapshotParticipant?.bVideoOn === true
}

function normalizeSnapshotParticipant(snapshotParticipant = {}, options = {}) {
  const userId = normalizeZoomUserId(
    snapshotParticipant?.userId ?? snapshotParticipant?.id,
  )

  if (!userId) {
    return null
  }

  const parsedMetadata = parseParticipantMetadata(
    snapshotParticipant?.participantMetadata ??
      snapshotParticipant?.metadata ??
      snapshotParticipant?.userKey ??
      snapshotParticipant?.userIdentity,
  )
  const metadata = {
    ...parsedMetadata,
    identityKey:
      normalizeOptionalString(parsedMetadata?.identityKey) ||
      normalizeOptionalString(snapshotParticipant?.userKey) ||
      normalizeOptionalString(snapshotParticipant?.userIdentity),
    displayName:
      normalizeOptionalString(parsedMetadata?.displayName ?? parsedMetadata?.name) ||
      normalizeOptionalString(snapshotParticipant?.displayName ?? snapshotParticipant?.userName),
    role:
      normalizeOptionalString(parsedMetadata?.role) ||
      (snapshotParticipant?.isHost ? 'host' : null) ||
      (snapshotParticipant?.isManager ? 'manager' : null),
  }
  const identity = deriveParticipantIdentity({
    userId,
    metadata,
    isLocal: options?.isLocal === true,
    localParticipantIdentity: options?.localParticipantIdentity,
    allowTransportFallback: options?.allowTransportFallback !== false,
  })

  if (!identity?.participantId) {
    return null
  }

  const screenSharing = snapshotParticipant?.sharerOn === true
  const videoEnabled = screenSharing || resolveZoomVideoEnabled(snapshotParticipant)
  const videoHandle = videoEnabled
    ? {
        stream: snapshotParticipant?.stream || null,
        userId: Number(snapshotParticipant?.userId),
        type: screenSharing ? 'share' : 'video',
        videoQuality: snapshotParticipant?.videoQuality || options?.videoQuality || null,
      }
    : null

  return {
    participantId: identity.participantId,
    userId: identity?.userId || null,
    actorType: identity?.actorType || null,
    actorId: identity?.actorId || null,
    displayName:
      identity?.displayName ||
      normalizeOptionalString(snapshotParticipant?.displayName ?? snapshotParticipant?.userName) ||
      `Participant ${userId}`,
    profilePicId:
      identity?.profilePicId ||
      normalizeOptionalString(metadata?.profilePicId ?? metadata?.avatarImageId),
    role: identity?.role || metadata?.role || 'participant',
    isLocal: options?.isLocal === true,
    connectionState: 'connected',
    audioMuted: !resolveZoomAudioEnabled(snapshotParticipant),
    videoMuted: !videoEnabled,
    screenSharing,
    videoHandle,
    renderKey: `zoom:${userId}:${screenSharing ? 'share' : 'video'}`,
    transport: createZoomTransport(userId, metadata, {
      transportIdentityFallback: identity?.transportIdentityFallback === true,
    }),
  }
}

function removeDetachedElements(detachedElements) {
  if (Array.isArray(detachedElements)) {
    detachedElements.forEach((element) => element?.remove?.())
    return
  }

  detachedElements?.remove?.()
}

export function createZoomVideoBinderPort() {
  return {
    getRenderKey(participant = {}) {
      return (
        normalizeOptionalString(participant?.renderKey) ||
        normalizeOptionalString(participant?.id) ||
        normalizeOptionalString(participant?.participantId) ||
        normalizeOptionalString(
          participant?.transport?.userId ?? participant?.transport?.zoomUserId,
        ) ||
        'zoom:participant'
      )
    },
    async bindVideo({ videoHandle, element }) {
      if (!videoHandle?.stream || !element || !Number.isFinite(Number(videoHandle?.userId))) {
        return
      }

      element.innerHTML = ''
      const attachMethod =
        videoHandle.type === 'share'
          ? videoHandle.stream.attachShareView
          : videoHandle.stream.attachVideo

      if (typeof attachMethod !== 'function') {
        return
      }

      const attachedElement = await Promise.resolve(
        videoHandle.type === 'share'
          ? attachMethod.call(videoHandle.stream, Number(videoHandle.userId))
          : attachMethod.call(
              videoHandle.stream,
              Number(videoHandle.userId),
              videoHandle.videoQuality,
            ),
      )

      if (attachedElement?.type) {
        return
      }

      attachedElement.style.width = '100%'
      attachedElement.style.height = '100%'
      attachedElement.style.display = 'block'
      attachedElement.style.objectFit = videoHandle.type === 'share' ? 'contain' : 'cover'
      element.appendChild(attachedElement)
    },
    async unbindVideo({ videoHandle, element, participant }) {
      const resolvedVideoHandle = videoHandle ?? participant?.videoHandle ?? null

      if (
        !resolvedVideoHandle?.stream ||
        !Number.isFinite(Number(resolvedVideoHandle?.userId))
      ) {
        if (element) {
          element.innerHTML = ''
        }
        return
      }

      const detachMethod =
        resolvedVideoHandle.type === 'share'
          ? resolvedVideoHandle.stream.detachShareView
          : resolvedVideoHandle.stream.detachVideo

      if (typeof detachMethod === 'function') {
        const detachedElements = await Promise.resolve(
          detachMethod.call(resolvedVideoHandle.stream, Number(resolvedVideoHandle.userId)),
        )
        removeDetachedElements(detachedElements)
      }

      if (element) {
        element.innerHTML = ''
      }
    },
  }
}

export function createZoomRtcPortBundle({
  initialConfig = {},
  sessionHandle = null,
  localParticipantIdentity = null,
} = {}) {
  const ownsSessionHandle = !sessionHandle
  const resolvedSessionHandle =
    sessionHandle || createZoomRtcSessionHandle({ initialConfig })
  const rtcEvents = createEventBus()
  const presenceEvents = createEventBus()

  let currentLocalParticipantIdentity = localParticipantIdentity || null

  function syncScreenShareConfigState(active) {
    const currentConfig =
      typeof resolvedSessionHandle?.getConfig === 'function'
        ? resolvedSessionHandle.getConfig()
        : null
    const currentEnabled = currentConfig?.rtcProps?.enableScreensharing === true

    if (active === currentEnabled) {
      return Promise.resolve(active)
    }

    return Promise.resolve(
      active === true
        ? resolvedSessionHandle.startScreenshare()
        : resolvedSessionHandle.stopScreenshare(),
    )
  }

  function emitParticipantSnapshot(snapshot) {
    const snapshotParticipants = Array.isArray(snapshot)
      ? snapshot
      : Array.isArray(snapshot?.participants)
        ? snapshot.participants
        : []

    const participants = snapshotParticipants
      .map((participant) =>
        normalizeSnapshotParticipant(participant, {
          isLocal: participant?.isLocal === true,
          localParticipantIdentity: currentLocalParticipantIdentity,
          videoQuality: snapshot?.videoQuality,
        }),
      )
      .filter(Boolean)

    rtcEvents.emit({
      type: RTC_SESSION_EVENT_TYPES.participantsChanged,
      participants,
    })
  }

  const rtcSession = {
    async join(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return resolvedSessionHandle.join()
    },
    async leave() {
      return resolvedSessionHandle.leave()
    },
    async setLocalAudioEnabled(enabled) {
      return resolvedSessionHandle.setAudioEnabled(enabled)
    },
    async setLocalVideoEnabled(enabled) {
      return resolvedSessionHandle.setVideoEnabled(enabled)
    },
    async setDevices(devices = {}) {
      const deviceTasks = []

      if (devices?.cameraDeviceId !== undefined) {
        deviceTasks.push(resolvedSessionHandle.switchCamera(devices.cameraDeviceId))
      }

      if (devices?.microphoneDeviceId !== undefined) {
        deviceTasks.push(resolvedSessionHandle.switchMicrophone(devices.microphoneDeviceId))
      }

      if (devices?.playbackDeviceId !== undefined) {
        deviceTasks.push(resolvedSessionHandle.setPlaybackDevice(devices.playbackDeviceId))
      }

      await Promise.all(deviceTasks)
      return true
    },
    async startScreenShare() {
      return resolvedSessionHandle.startScreenshare()
    },
    async stopScreenShare() {
      return resolvedSessionHandle.stopScreenshare()
    },
    subscribe(listener) {
      return rtcEvents.subscribe(listener)
    },
  }

  const presence = {
    async connect(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return true
    },
    async disconnect() {
      return true
    },
    async publishLocalParticipantMetadata(metadata = {}) {
      currentLocalParticipantIdentity = {
        ...(currentLocalParticipantIdentity || {}),
        ...metadata,
      }

      const localParticipant = normalizeLocalParticipantIdentity(
        currentLocalParticipantIdentity,
      )

      if (localParticipant) {
        presenceEvents.emit({
          type: PRESENCE_EVENT_TYPES.participantMetadataChanged,
          participant: {
            ...localParticipant,
            isLocal: true,
          },
          metadata: {
            ...localParticipant,
            isLocal: true,
          },
          admitToRoster: false,
        })
      }

      return localParticipant
    },
    subscribe(listener) {
      return presenceEvents.subscribe(listener)
    },
  }

  const compatibilityCallbacks = {
    'participants-updated': (snapshot) => {
      emitParticipantSnapshot(snapshot)
    },
    'screen-share-state-changed': (active) => {
      syncScreenShareConfigState(active === true).catch(() => {})
    },
    'connection-state-change': (sessionState, previousState, reason) => {
      rtcEvents.emit({
        type: RTC_SESSION_EVENT_TYPES.stateChanged,
        sessionState,
        previousState,
        reason,
      })
    },
  }

  return {
    sessionHandle: resolvedSessionHandle,
    rtcSession,
    presence,
    videoBinder: createZoomVideoBinderPort(),
    compatibilityCallbacks,
    async destroy() {
      rtcEvents.clear()
      presenceEvents.clear()

      if (ownsSessionHandle) {
        await resolvedSessionHandle.destroy()
      }
    },
  }
}
